import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Country } from '../components/select-country/select-country.data';
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs";
import { STORE } from "../utils/constants";
import { ParentalGateService } from "../services/parental-gate.service";
import { i18nUrl } from "../utils/lang";

export interface Lang {
  invalid_promo_code_instructions: string;
  valid_promo_code_instructions: string;
  identity_cgv_url: string
  identify_privacy_url: string
}

@Injectable({
  providedIn: 'root',
})
export class UnityService {
  private setCountryCallBackUrl = environment.appKey + '://action/user/country/set?value=';
  private userLoginCallBackUrl = environment.appKey + '://action/user/logIn?value=';
  private confirmUserLoggedCallbackUrl = environment.appKey + '://action/user/subsribed?uuid=';
  private unityHomeCallBackUrl = environment.appKey + '://home?version=3&country=';
  private setIdCallBackUrl = environment.appKey + '://action/user/set?uuid=';
  private searchCallBackUrl = environment.appKey + '://search?query=';
  private accountCallBackUrl = environment.appKey + '://action/user/account/back';
  private logoutCallBackUrl = environment.appKey + '://event/user/signed-out';
  private confirmDeletedUserCallBackUrl = environment.appKey + '://event/user/deleted';
  private userSubscriberCallBackUrl = environment.appKey + '://action/user/subsribed';
  private changeCountryCallBackUrl = environment.appKey + '://action/user/country/change';
  private clearCallbackUrl = environment.appKey + "://clearPartnerCookie?domains=https://pass.canalplus.com,https://data.canalplus.com";
  private iapCAllBack = environment.appKey + "://action/iap/initiate";
  private cgvCAllBack = environment.appKey + '://action/url/open/externally?url=';
  private iapRestore = environment.appKey + '://action/iap/restore';
  private bookLink = environment.appKey + '://home?version=3&country={{country}}&referrer=IDENTIFY';
  private actionDeletedUserCallBackUrl = environment.appKey + '://action/user/deleted';
  private actionValidePromoCodeCallBackUrl = environment.appKey + '://action/user/valid/offer'; // TODO mettre à jour ;

  private readonly webOnly = environment.webOnly ?? false;

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private parentalGateService: ParentalGateService
  ) { }

  private navigate(url: string) {
    if (this.webOnly) {
      console.log('[webOnly] skipping deep link:', url);
      return;
    }
    window.location.href = url;
  }

  // Links opened outside the app (Safari) must go through the parental gate (App Store requirement).
  private openExternally(externalUrl: string) {
    this.parentalGateService.guard(() => this.navigate(`${this.cgvCAllBack}` + externalUrl));
  }

  setCountryToUnity(country: Country) {
    const url = this.setCountryCallBackUrl + country.code.toUpperCase() + '&legacy=' + country.legacy;
    console.log('setCountryToUnity', url);
    this.navigate(url);
  }

  setUserIdToUnity(userUuid: string) {
    const url = this.setIdCallBackUrl + userUuid;
    console.log('setUserIdToUnity', url);
    this.navigate(url);
  }

  clearCanalCookies() {
    const url = this.clearCallbackUrl;
    console.log('clearCallbackUrl', url);
    this.navigate(url);
  }

  userLoginToUnity(userUuid: string) {
    const url = this.userLoginCallBackUrl + userUuid;
    console.log('userLoginToUnity', url);
    this.navigate(url);
  }

  confirmUserLoggedToUnity(userUuid: string) {
    const url = `${this.confirmUserLoggedCallbackUrl}${userUuid}&message='Bravo,...'`;
    console.log('confirmUserLoggedToUnity', url);
    this.navigate(url);
  }

  gotoUnityHome(countryCode: string) {
    const url = `${this.unityHomeCallBackUrl}${countryCode.toUpperCase()}&referrer=app.wakatoon.com/intro`;
    console.log('legacy country', url);
    this.navigate(url);
  }

  gotoUnitySearchResult(keyword: string) {
    const url = `${this.searchCallBackUrl}${keyword.toUpperCase()}&referrer=SCREEN_COUNTRY_SELECT`;
    console.log('gotoUnitySearchResult', url);
    this.navigate(url);
  }

  gotoUnityAccountBack() {
    const url = `${this.accountCallBackUrl}`;
    console.log('gotoUnityAccountBack', url);
    this.navigate(url);
  }

  gotoUnityLogout() {
    const url = `${this.logoutCallBackUrl}`;
    console.log('gotoUnityLogout', url);
    this.navigate(url);
  }

  gotoConfirmDeletedUser() {
    const url = `${this.confirmDeletedUserCallBackUrl}`;
    console.log('gotoConfirmDeletedUser', url);
    this.navigate(url);
  }

  actionDeletedUser() {
    const url = `${this.actionDeletedUserCallBackUrl}`;
    console.log('actionDeletedUserCallBackUrl', url);
    this.navigate(url);
  }

  gotoUserSubscriber() {
    const url = `${this.userSubscriberCallBackUrl}`;
    console.log('gotoUserSubscriber', url);
    this.navigate(url);
  }

  changeCountry() {
    const url = `${this.changeCountryCallBackUrl}`;
    console.log('changeCountry', url);
    this.navigate(url);
  }

  activateNatif() {
    const url = `${this.iapCAllBack}`;
    console.log('activateNatif', url);
    this.navigate(url);
  }

  cgv() {
    this.http.get<Lang>(i18nUrl(this.translateService.currentLang))
      .subscribe(value => {
        console.log('cgv', value.identity_cgv_url);
        this.openExternally(value.identity_cgv_url);
      })
  }

  privacyPolicy() {
    this.http.get<Lang>(i18nUrl(this.translateService.currentLang))
      .subscribe(value => {
        console.log('privacyPolicy', value.identify_privacy_url);
        this.openExternally(value.identify_privacy_url);
      })
  }

  booklink(referrerLink: string) {
    const countryCode = (JSON.parse(localStorage.getItem(STORE.USER_COUNTRY)!) as Country).code;

    this.bookLink = this.bookLink.replace("{{country}}", countryCode!)
    this.bookLink = this.bookLink.replace("IDENTIFY", referrerLink)
    const url = `${this.bookLink}`;
    console.log('openBook link', url);
    this.navigate(url);
  }

  callNatifIap() {
    const url = `${this.iapRestore}`;
    console.log('callNatifIap', url);
    this.navigate(url);
  }

  goToUrl(value: string) {
    console.log('Go to Url', value);
    this.openExternally(value);
  }

  gotoUnityAfterValidPromo(oneTimeToken: string) {
    const url = `${this.actionValidePromoCodeCallBackUrl}` + oneTimeToken;
    console.log('gotoUnityAfterValidPromo', url);
    this.navigate(url);
  }


}
